import { useState, useEffect, useRef, useCallback } from 'react';

const galleryImages = [
  {
    src: 'https://storage.readdy-site.link/project_files/1f9d1016-e227-41f8-97ce-9f3c971bd6de/5d4334e6-dc31-4261-b04b-848481c46451_DSC_0188.jpg?v=c8a7bcd897329cf1925e1d1a582b0439',
    caption: 'IIT Roorkee Presentation',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/1f9d1016-e227-41f8-97ce-9f3c971bd6de/73af35f4-70db-4554-8f42-d966b900e004_DSC_0193.jpg?v=6d52c8fd52d28c2c31daa5b9ca95e133',
    caption: 'Workshop Moments',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/1f9d1016-e227-41f8-97ce-9f3c971bd6de/6d3d1ac7-e8a6-416f-b1fa-8b7194788411_DSC_0205.jpg?v=fa4f309a072746b290d4b76a69d16310',
    caption: 'Research Discussion',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/1f9d1016-e227-41f8-97ce-9f3c971bd6de/a3ed4949-568d-42e9-b8c1-38712062ac7f_DSC_0059-2.jpg?v=2c203c501d803941c13721a7b7a15d95',
    caption: 'Project Recognition',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/1f9d1016-e227-41f8-97ce-9f3c971bd6de/5173c819-5154-4334-834f-e719beae904b_DSC_0177.jpg?v=fcabd2a892aa5506eab75af7dfaa4b41',
    caption: 'Team at IIT Roorkee',
  },
  {
    src: 'https://storage.readdy-site.link/project_files/1f9d1016-e227-41f8-97ce-9f3c971bd6de/4b0795c2-131d-4bac-a889-94d3b43ae2a1_DSC_0185.jpg?v=0bd017e4620f66844d4cbc1f1183ec0d',
    caption: 'Campus & Ceremony',
  },
];

export default function IITGallery() {
  const [activeIdx, setActiveIdx] = useState<number>(0);
  const [fade, setFade] = useState<boolean>(true);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.gallery-animate').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).classList.add('opacity-100', 'translate-y-0');
                (el as HTMLElement).classList.remove('opacity-0', 'translate-y-8');
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const changeImage = useCallback((newIdx: number) => {
    setFade(false);
    setTimeout(() => {
      setActiveIdx(newIdx);
      setFade(true);
    }, 200);
  }, []);

  const prev = () => changeImage((activeIdx - 1 + galleryImages.length) % galleryImages.length);
  const next = () => changeImage((activeIdx + 1) % galleryImages.length);

  return (
    <section id="iit" className="py-24 md:py-32 bg-[#EDE0D4]/40" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Header */}
        <div className="text-center mb-14 gallery-animate opacity-0 translate-y-8 transition-all duration-700">
          <p className="text-[#2A9D8F] text-sm uppercase tracking-[0.25em] font-semibold mb-3">Academic Milestone</p>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#1C1C1C] mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Recognized at IIT Roorkee
          </h2>
          <p className="text-[#6C757D] text-base max-w-xl mx-auto">
            Presenting &lsquo;Echoes of the Ganga&rsquo; at one of India&apos;s premier institutes&nbsp; showcasing data-driven insights on river pollution and environmental impact.
          </p>
        </div>

        {/* Main expanded image */}
        <div className="gallery-animate opacity-0 translate-y-8 transition-all duration-700 relative rounded-3xl overflow-hidden mb-10 bg-[#1C1C1C]" style={{ height: '520px' }}>
          <img
            src={galleryImages[activeIdx].src}
            alt={galleryImages[activeIdx].caption}
            className={`w-full h-full object-contain bg-[#1C1C1C] transition-opacity duration-200 ${fade ? 'opacity-100' : 'opacity-0'}`}
          />
          {/* Caption bar */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#1B4332]/80 to-transparent px-8 py-6 flex items-end justify-between">
            <div>
              <p className="text-white/60 text-sm mt-1">{activeIdx + 1} of {galleryImages.length}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={prev}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-[#F4A261]/40 text-white transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-xl" />
              </button>
              <button
                onClick={next}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-[#F4A261]/40 text-white transition-colors cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-xl" />
              </button>
            </div>
          </div>
        </div>

        {/* IIT badge */}
        <div className="gallery-animate opacity-0 translate-y-8 transition-all duration-700 mt-10 flex justify-center">
          <div className="inline-flex items-center gap-3 bg-[#F8F5F0] border border-[#2A9D8F]/30 rounded-full px-6 py-3">
            <i className="ri-award-line text-[#F4A261] text-lg" />
            <span className="text-[#1B4332] text-sm font-medium">Presented &amp; Recognized at IIT Roorkee Workshop</span>
          </div>
        </div>

      </div>
    </section>
  );
}
